import React from "react";
import { motion } from "framer-motion";
import { Sparkles, Cpu, BookOpen, Compass, ClipboardCheck, BarChart4 } from "lucide-react";

export default function SaasAIFeatures() {
  const aiFeatures = [
    {
      title: "AI Personal Tutor",
      desc: "An intelligent learning companion providing 24/7 custom course tutoring, concept explanations, and personalized revision schedules for students.",
      icon: <Cpu size={24} />,
      color: "from-violet-500 to-indigo-500",
      glow: "rgba(124, 58, 237, 0.15)"
    },
    {
      title: "AI Question Generator",
      desc: "Autocreate full question sets (MCQ, short questions, essays) matching custom academic syllabus criteria and targeted difficulty parameters.",
      icon: <Sparkles size={24} />,
      color: "from-blue-500 to-indigo-500",
      glow: "rgba(59, 130, 246, 0.15)"
    },
    {
      title: "AI Exam Creator",
      desc: "Instantly draft entire competitive exam blueprints. Auto-distributes structured test copies directly to students based on schedule rules.",
      icon: <BookOpen size={24} />,
      color: "from-cyan-500 to-blue-500",
      glow: "rgba(6, 182, 212, 0.15)"
    },
    {
      title: "AI Subjective Evaluation",
      desc: "Automate written exam evaluations. Detect handwriting, check answer keys, compute standard marks draft, and provide students descriptive revision tips.",
      icon: <ClipboardCheck size={24} />,
      color: "from-emerald-500 to-teal-500",
      glow: "rgba(16, 185, 129, 0.15)"
    },
    {
      title: "AI Adaptive Study Planner",
      desc: "Create dynamic student calendar programs. Analyzes weaknesses to schedule custom practice tests and reading times for curriculum success.",
      icon: <Compass size={24} />,
      color: "from-amber-500 to-orange-500",
      glow: "rgba(245, 158, 11, 0.15)"
    },
    {
      title: "AI Deep Analytics",
      desc: "Synthesize large marks collections, attendance percentages, and test frequencies. Predict student dropouts or exam failures weeks ahead.",
      icon: <BarChart4 size={24} />,
      color: "from-rose-500 to-pink-500",
      glow: "rgba(244, 63, 94, 0.15)"
    }
  ];

  return (
    <section className="py-24 bg-white dark:bg-slate-950 relative overflow-hidden">
      {/* Background gradients */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-to-r from-violet-600/5 to-cyan-500/5 rounded-full blur-[140px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-20">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-violet-50 dark:bg-violet-950/40 border border-violet-100 dark:border-violet-900/50 rounded-full text-violet-600 dark:text-violet-400 text-sm font-bold mb-4">
            <Sparkles size={16} className="animate-pulse" />
            <span>Futuristic Education AI</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black text-slate-900 dark:text-white tracking-tight">
            Meet Your AI Teaching Assistant
          </h2>
          <p className="mt-4 text-lg text-slate-600 dark:text-slate-300 font-medium">
            Automate tedious teaching tasks and provide personalized support to every student. Our AI models act as co-pilots for administrators and tutors.
          </p>
        </div>

        {/* AI Cards Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {aiFeatures.map((item, i) => (
            <motion.div
              key={item.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-50px" }}
              transition={{ duration: 0.6, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
              whileHover={{ 
                y: -6, 
                boxShadow: `0 20px 40px -15px ${item.glow}`,
                borderColor: "rgba(139, 92, 246, 0.4)"
              }}
              className="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-850 rounded-[2rem] p-8 transition-all duration-300 relative group flex flex-col justify-between"
            >
              <div>
                {/* Glowing sphere behind icon */}
                <div className="absolute top-8 left-8 w-12 h-12 rounded-full blur-xl group-hover:scale-150 transition-transform duration-500" style={{ backgroundColor: item.glow }} />
                
                {/* Icon */}
                <div className={`w-12 h-12 rounded-2xl bg-gradient-to-br ${item.color} text-white flex items-center justify-center relative z-10 shadow-lg`}>
                  {item.icon}
                </div>

                <h3 className="text-xl font-black text-slate-900 dark:text-white mt-8 mb-4">
                  {item.title}
                </h3>
                
                <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold leading-relaxed">
                  {item.desc}
                </p>
              </div>

              {/* Bottom decorative divider/line */}
              <div className="w-8 h-1 bg-gradient-to-r from-violet-600 to-indigo-600 rounded-full mt-8 group-hover:w-full transition-all duration-500" />
            </motion.div>
          ))}
        </div>

      </div>
    </section>
  );
}
